import {
  effect,
  inject,
  Injector,
  makeEnvironmentProviders,
  provideAppInitializer,
  type EnvironmentProviders,
} from '@angular/core';
import { environment } from '@env';
import { AuthService } from '@core/auth/auth.service';
import { ConsentService } from '@core/analytics/consent.service';
import type { OaiqFn } from './openai-pixel.types';

/** Installs the window.oaiq queue and injects the OpenAI measurement pixel SDK. Idempotent. */
export function loadOpenAiPixel(): void {
  if (typeof window === 'undefined' || typeof window.oaiq === 'function') return;

  const oaiq: OaiqFn = function oaiq(...args: unknown[]) {
    oaiq.q!.push(args);
  };
  oaiq.q = [];
  window.oaiq = oaiq;

  const script = document.createElement('script');
  script.async = true;
  script.dataset['oaiq'] = '';
  script.src = 'https://bzrcdn.openai.com/sdk/oaiq.min.js';
  document.head.appendChild(script);
}

/** Lowercase hex SHA-256 of the trimmed value — OpenAI only accepts hashed identifiers. */
export async function sha256Hex(value: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(value.trim()));
  return Array.from(new Uint8Array(digest), (b) => b.toString(16).padStart(2, '0')).join('');
}

/** Error-isolated call into window.oaiq so analytics never breaks the app. */
function call(...args: unknown[]): void {
  try {
    window.oaiq?.(...args);
  } catch {
    /* analytics failures must never surface to the user */
  }
}

export interface OpenAiPixelDeps {
  auth: AuthService;
  consent: ConsentService;
  injector: Injector;
}

/** Loads the pixel, mirrors analytics consent, and re-inits with the hashed employer id after login. */
export function initOpenAiPixel(pixelId: string, deps: OpenAiPixelDeps): void {
  if (!pixelId) return;

  loadOpenAiPixel();
  // Consent must be set before init so a persisted opt-out never sends a ping.
  call('consent', deps.consent.enabled());
  call('init', { pixelId });

  effect(() => call('consent', deps.consent.enabled()), { injector: deps.injector });

  effect(
    () => {
      const employer = deps.auth.employer();
      if (!employer) return;
      // Re-calling init with the full user object is the documented way to attach identity after login.
      void sha256Hex(employer.id).then((external_id_sha256) => call('init', { pixelId, user: { external_id_sha256 } }));
    },
    { injector: deps.injector },
  );
}

/** Provider entry point — add `provideOpenAiPixel()` to the app.config.ts providers array. */
export function provideOpenAiPixel(): EnvironmentProviders {
  return makeEnvironmentProviders([
    provideAppInitializer(() => {
      initOpenAiPixel(environment.openAiPixelId, {
        auth: inject(AuthService),
        consent: inject(ConsentService),
        injector: inject(Injector),
      });
    }),
  ]);
}
